/* eslint-disable @typescript-eslint/restrict-template-expressions */
import styled from "@emotion/styled";
import { ThemeKey, space } from "@smartsheet/design-tokens";
import { createFocusRingStyles } from "../../../../util/createFocusRingStyles";
import { Clickable } from "../../../clickable";
import { FocusMode } from "../../../focusMode";
import { DAY_STYLED_CLASS_NAME } from "../components/DatePickerDay";
import { datePickerTheme } from "./datePickerTheme";
import { getDisabledBumperStyles } from "./datePickerTheme";

export interface DatePickerDayButtonStyledProps {
    focusMode: FocusMode;
    isDisabled: boolean;
    isSelected: boolean;
    isToday: boolean;
    themeMode: ThemeKey;
    isInRange?: boolean;
}

const shouldForwardProp = (propName: string): boolean => {
    switch (propName) {
        // NOT blocking `isDisabled` so it gets forwarded on to the Clickable.
        case "focusMode":
        case "isSelected":
        case "isToday":
            // Just add any extra props here :)
            return false;
    }
    return true;
};

const getKeyboardStyles = (isSelected: boolean, _isToday: boolean, themeMode: ThemeKey) => `
    &:focus {
        background-color: ${datePickerTheme.backgroundColor};
        // Make sure a selected element shows above its peers
        // so the focus ring will not be occluded.
        z-index: 1;
        box-shadow: 0px 0px 0px 3px #ffffff;
    }

    ${
        isSelected
            ? `
                &,
                &:focus {
                    background-color: ${datePickerTheme.dayBackgroundColorSelected(themeMode)};

                    & .${DAY_STYLED_CLASS_NAME} {
                        color: ${datePickerTheme.dayColorSelected(themeMode)};
                        font-weight: ${datePickerTheme.dayFontWeightSelected(themeMode)};
                    }
                }                   
            `
            : ""
    }
`;

const getMouseStyles = (isSelected: boolean, isToday: boolean, themeMode: ThemeKey) => `

    &:hover {
        background-color: ${
            isToday
                ? datePickerTheme.dayBackgroundColorTodayHover(themeMode)
                : datePickerTheme.dayBackgroundColorHover(themeMode)
        };
    }

    &:active {
        background-color: ${
            isToday
                ? datePickerTheme.dayBackgroundColorTodayActive(themeMode)
                : datePickerTheme.dayBackgroundColorActive(themeMode)
        };
    }

    ${
        isSelected
            ? `
            &,
            &:hover,
            &:active {
                background-color: ${datePickerTheme.dayBackgroundColorSelected(themeMode)};

                & .${DAY_STYLED_CLASS_NAME} {
                    color: ${datePickerTheme.dayColorSelected(themeMode)};
                    font-weight: ${datePickerTheme.dayFontWeightSelected(themeMode)};
                }
            }
        `
            : ""
    }
`;

const getDisabledStyles = ({ isDisabled, themeMode }: DatePickerDayButtonStyledProps) =>
    isDisabled
        ? `
            cursor: not-allowed;

            &,
            &:focus,
            &:hover,
            &:active {
                background-color: transparent;

                & .${DAY_STYLED_CLASS_NAME} {
                    color: ${datePickerTheme.dayColorDisabled(themeMode)};
                    ${getDisabledBumperStyles({ themeMode })}
                }
            }
        `
        : "";

export const DatePickerDayButtonStyled = styled(Clickable, {
    shouldForwardProp,
})<DatePickerDayButtonStyledProps>`
    box-sizing: border-box;
    border: 2px solid transparent;
    border-radius: ${({ themeMode }) => datePickerTheme.dayBorderRadius(themeMode)};

    height: ${space.space60};
    width: ${({ themeMode }) => datePickerTheme.dayWidth(themeMode)};
    color: ${({ themeMode }) => datePickerTheme.dayColor(themeMode)};

    ${({ isInRange, isToday, themeMode }) =>
        isToday &&
        `
            ${!isInRange ? `background-color: ${datePickerTheme.dayBackgroundColorToday(themeMode)};` : ""};
            border-color: ${datePickerTheme.dayBorderColorToday(themeMode)};
            & .${DAY_STYLED_CLASS_NAME} {
                color: ${datePickerTheme.dayColorToday(themeMode)};
                font-weight: ${datePickerTheme.dayFontWeightToday(themeMode)};
            }
        `}
    // We need to treat a LOT of the styles differently if we are in keyboard or mouse mode.
    ${({ focusMode, isSelected, isToday, themeMode }) =>
        focusMode === "keyboard"
            ? getKeyboardStyles(isSelected, isToday, themeMode)
            : getMouseStyles(isSelected, isToday, themeMode)}
    ${({ themeMode }) =>
        createFocusRingStyles({
            theme: themeMode,
            gap: datePickerTheme.dayFocusRingGap(themeMode),
            componentBorderWidth: 2,
            componentRadius: datePickerTheme.dayBorderRadius(themeMode),
            secondaryWidth: 0,
        })}
    // Putting this last so it always wins.
    ${getDisabledStyles};
`;
